import { useState, useEffect } from 'react';
import ErrorHandler from '@Root/ErrorHandler';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import { SnackbarManager } from '@CommonFunctionalDisplay/GlobalSnackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import CircularProgress from '@mui/material/CircularProgress';
import { MdLockOutline } from 'react-icons/md';
import './EditUserPermissionsModal.scss';

// A user's project permission has no explicit record until they're granted one, so when a
// row is missing a field we fall back to this default policy rather than leaving it blank:
// everything on except Edit, which an admin has to opt a user into explicitly.
const DEFAULT_PERMISSIONS = { allowView: true, allowEdit: false, allowAdmin: true, allowDelete: true };

const PERMISSION_ROWS = [
    { key: 'allowView', token: 'read', label: 'View', description: 'Can see this project and its tickets' },
    { key: 'allowEdit', token: 'write', label: 'Edit', description: 'Can create and edit tickets in this project' },
    { key: 'allowAdmin', token: 'admin', label: 'Admin', description: 'Can manage members, teams, and project settings' },
    { key: 'allowDelete', token: 'owner', label: 'Delete', description: 'Can delete tickets in this project' },
];

export default function EditUserPermissionsModal({ open, onClose, user, projectId, isAdmin, onSaved }) {
    const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
    const [saving, setSaving] = useState(false);

    const projectService = new ProjectService();
    const errorHandler = new ErrorHandler();

    useEffect(() => {
        if (!open || !user) return;
        setPermissions({
            allowView: user.allowView ?? DEFAULT_PERMISSIONS.allowView,
            allowEdit: user.allowEdit ?? DEFAULT_PERMISSIONS.allowEdit,
            allowAdmin: user.allowAdmin ?? DEFAULT_PERMISSIONS.allowAdmin,
            allowDelete: user.allowDelete ?? DEFAULT_PERMISSIONS.allowDelete,
        });
    }, [open, user]);

    const togglePermission = (key) => {
        setPermissions(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSave = async () => {
        const userId = user?.userId ?? user?.id;
        if (!userId || !projectId) return;
        try {
            setSaving(true);
            const tokens = PERMISSION_ROWS.filter(row => permissions[row.key]).map(row => row.token);
            await projectService.updateContributorPermissionInProject({
                childType: 'user',
                userId,
                projectId: Number(projectId),
                permissions: JSON.stringify(tokens),
            });
            SnackbarManager.show?.('Permissions updated', 'success');
            onSaved?.();
            onClose();
        } catch (err) {
            errorHandler.handleError(err);
            SnackbarManager.show?.('Failed to update permissions', 'error');
        } finally {
            setSaving(false);
        }
    };

    const displayName = user?.fullName || user?.userName || 'this user';
    const canEdit = !!isAdmin;

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            className="editUserPermissionsModal"
            PaperProps={{ className: 'editUserPermissionsModal__paper' }}
        >
            <DialogTitle className="editUserPermissionsModal__title">
                <div className="editUserPermissionsModal__titleRow">
                    <div className="editUserPermissionsModal__titleIcon">
                        <MdLockOutline />
                    </div>
                    <div>
                        <div className="editUserPermissionsModal__titleText">
                            Permissions
                        </div>
                        <div className="editUserPermissionsModal__titleSub">
                            {displayName}{user?.userName ? ` · @${user.userName}` : ''}
                        </div>
                    </div>
                </div>
            </DialogTitle>

            <DialogContent className="editUserPermissionsModal__content">
                {!canEdit && (
                    <p className="editUserPermissionsModal__readOnlyHint">
                        Only project admins can change permissions.
                    </p>
                )}
                <div className="editUserPermissionsModal__rows">
                    {PERMISSION_ROWS.map(row => (
                        <div key={row.key} className="editUserPermissionsModal__row">
                            <div className="editUserPermissionsModal__row__info">
                                <div className="editUserPermissionsModal__row__label">{row.label}</div>
                                <div className="editUserPermissionsModal__row__description">{row.description}</div>
                            </div>
                            <Switch
                                checked={!!permissions[row.key]}
                                onChange={() => togglePermission(row.key)}
                                disabled={!canEdit || saving}
                                className="editUserPermissionsModal__row__switch"
                            />
                        </div>
                    ))}
                </div>
            </DialogContent>

            <DialogActions className="editUserPermissionsModal__actions">
                <Button onClick={onClose} variant="text" disabled={saving}>
                    {canEdit ? 'Cancel' : 'Close'}
                </Button>
                {canEdit && (
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving
                            ? <><CircularProgress size={13} style={{ marginRight: 6 }} />Saving...</>
                            : 'Save Changes'}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
